/* Shared by the header search box and the /search results page. */

const BASE_API = (process.env.REACT_APP_BASE_API || "").replace(/\/+$/, "");

export const toImageUrl = (src) => {
  const s = typeof src === "string" ? src : src?.url;
  if (!s) return null;
  if (/^(https?:)?\/\//.test(s) || s.startsWith("data:")) return s;
  const clean = String(s).replace(/^\/+/, "");
  return clean.includes("/") ? `${BASE_API}/${clean}` : `${BASE_API}/uploads/${clean}`;
};

// API may return [] or { data: [] } or { products: [] }
const extractList = (json) => {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.products)) return json.products;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.data?.products)) return json.data.products;
  if (Array.isArray(json?.items)) return json.items;
  return [];
};

// If your API field names are different, change them only here
export const normalizeProduct = (p) => ({
  id: p.id ?? p._id,
  name: String(p.name ?? p.title ?? ""),
  category: String(
    p.category_name ??
      p.categoryName ??
      p.category?.name ??
      (typeof p.category === "string" ? p.category : ""),
  ),
  description: String(p.description ?? p.details ?? ""),
  price: Number(p.price ?? p.sellingPrice ?? p.salePrice ?? 0),
  rating: Number(p.rating ?? p.averageRating ?? 0),
  img: toImageUrl(p.image ?? p.img ?? p.thumbnail ?? (Array.isArray(p.images) ? p.images[0] : null)),
});

/* One request is shared by every search box and refreshed after 60 seconds */
let cache = null;
let cachedAt = 0;
let inflight = null;

export const loadProducts = () => {
  if (cache && Date.now() - cachedAt < 60000) return Promise.resolve(cache);
  if (inflight) return inflight;

  inflight = fetch(`${BASE_API}/products`)
    .then((res) => {
      if (!res.ok) throw new Error("Could not load products");
      return res.json();
    })
    .then((json) => {
      cache = extractList(json).map(normalizeProduct);
      cachedAt = Date.now();
      return cache;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
};

/* Every word typed must appear in the name, category or description.
   Name matches rank first, then category, then description. */
export const searchProducts = (list, query) => {
  const terms = String(query).toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];

  return list
    .map((p) => {
      const name = p.name.toLowerCase();
      const cat = p.category.toLowerCase();
      const desc = p.description.toLowerCase();
      if (!terms.every((t) => `${name} ${cat} ${desc}`.includes(t))) return null;

      let score = 0;
      terms.forEach((t) => {
        if (name.startsWith(t)) score += 4;
        else if (name.includes(t)) score += 3;
        else if (cat.includes(t)) score += 2;
        else score += 1;
      });
      return { p, score };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.p);
};

export const formatPrice = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
